import AngleLeft from '@alefeducation/ui-elements-library-icons/lib/components/Icons/AngleLeft'
import AngleRight from '@alefeducation/ui-elements-library-icons/lib/components/Icons/AngleRight'
import React, { useEffect, useRef, useState } from 'react'
import { CalendarProps as ReactCalendarProps } from 'react-calendar'
import { createPortal } from 'react-dom'
import { Times } from '@alefeducation/ui-elements-library-icons'
import { Button, theme } from '@alefeducation/ui-elements-library-core'
import { DatePickerWrapper, StyledCalendar } from './DatePicker.styled'
import useOutsideClickListener from './useOutsideClickListener'

const POSITION_X_OFFSET = -220
const POSITION_Y_OFFSET = 10

export type DateTuple = [Date | null, Date | null]

export type CalendarProps = {
  value: DateTuple
  isOpen: boolean
  onChange: (value: DateTuple) => void
  onClose: () => void
  minDate?: Date
  maxDate?: Date
  customCalendarProps?: Partial<ReactCalendarProps>
  children: React.ReactNode
}

export const DatePicker = ({ isOpen, value, onChange, onClose, minDate, maxDate, children, customCalendarProps }: CalendarProps) => {
  const [cords, setCords] = useState<{ x: number; y: number }>()
  const calendarRef = useRef<HTMLDivElement>()
  const childrenRef = useRef<HTMLDivElement>()
  const [selectedDates, setSelectedDates] = useState<DateTuple>()
  useOutsideClickListener([calendarRef], onClose)
  useEffect(() => {
    setSelectedDates([
      value[0] ? new Date(value[0].getFullYear(), value[0].getMonth(), value[0].getDate()) : new Date(),
      value[1] ? new Date(value[1].getFullYear(), value[1].getMonth(), value[1].getDate()) : null
    ])
  }, [value])
  useEffect(() => {
    const onResize = () => {
      if (childrenRef?.current) {
        const { left, top } = childrenRef.current.getBoundingClientRect()
        const width = childrenRef.current.offsetWidth
        const height = childrenRef.current.offsetHeight
        setCords({ x: left + width / 2 + POSITION_X_OFFSET, y: top + height + POSITION_Y_OFFSET })
      }
    }
    window.addEventListener('resize', onResize)
    onResize()
    return () => window.removeEventListener('resize', onResize)
  }, [childrenRef.current, isOpen])

  return (
    <>
      <div ref={childrenRef}>{children}</div>
      {isOpen &&
        createPortal(
          <DatePickerWrapper data-testid="date-picker-wrapper" x={cords?.x} y={cords?.y} className="date-picker-wrapper" ref={calendarRef}>
            <div className="date-picker-close-icon">
              <Times data-testid="close-date-picker-icon" onClick={onClose} color="primary.40" />
            </div>
            <StyledCalendar
              minDetail="month"
              minDate={minDate}
              maxDate={maxDate || new Date()}
              selectRange
              allowPartialRange
              formatShortWeekday={(locale, date) => date.toDateString()[0]}
              prevLabel={<AngleLeft color="#485465" height={12} width={12} />}
              prev2Label={null}
              nextLabel={<AngleRight color="#485465" height={12} width={12} />}
              next2Label={null}
              onChange={selected => {
                setSelectedDates(selected as DateTuple)
              }}
              defaultValue={minDate}
              value={selectedDates}
              {...customCalendarProps}
            />
            <div className="date-picker-buttons">
              <Button
                style={{
                  border: `1px solid ${theme.colors.primary[40]}`
                }}
                variant="ghost"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  onChange(selectedDates)
                  onClose()
                }}
              >
                Select Dates
              </Button>
            </div>
          </DatePickerWrapper>,
          document.body
        )}
    </>
  )
}
